import { Box, useTheme } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { GapBox } from 'components/Box'
import {
  BoxForm,
  NumberField,
  RadioGroup,
  Select,
  SelectAutocomplete,
  StackForm,
  TextField,
} from 'components/Form'
import { SectionHeader } from 'components/Header'
import Hint from 'components/Hint'
import { Link } from 'components/Link'
import Paper from 'components/Paper'
import { useIsFieldDisabled } from 'features/globalSetup/forms/Prebid/Bidders/hooks/useIsFieldDisabled'
import { BidderSlice, CommonFormProps } from 'features/globalSetup/forms/Prebid/Bidders/types'
import { useGetPrioritiesForSelect } from 'features/globalSetup/hooks/useGetPrioritiesForSelect'
import { useGetYieldbirdStatusesForSelect } from 'features/globalSetup/hooks/useGetYieldbirdStatusesForSelect'
import { YieldbirdStatus } from 'features/globalSetup/types/yieldbirdStatus'
import { yieldbirdStatusColor } from 'features/globalSetup/utils/status'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { generateSelector } from 'providers/product-fruits/utils'
import { ROUTES } from 'routes'
import { SELECT_HINT_LEARN_MORE } from './constants'
import { useAccountTypeOptions } from './hooks/useAccountTypeOptions'
import { CommonSchema } from './schemas/commonSchema'
import { useGetConsentModesForSelect } from './sections/hooks/useGetConsentModesForSelect'
import { useGetSupplyChainsForSelect } from './sections/hooks/useGetSupplyChainsForSelect'

export const CommonForm = ({ bidders, variant }: CommonFormProps) => {
  const { workspaceId } = useWorkspaceParam()
  const theme = useTheme()
  const { isFieldDisabled } = useIsFieldDisabled()
  const { control } = useFormContext<CommonSchema>()
  const { t: tc } = useTranslation(['features/globalSetup', 'common'])
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.bidders.add',
  })

  const { accountType, bidderId } = useWatch<CommonSchema>()
  const accountTypeOptions = useAccountTypeOptions()
  const statuses = useGetYieldbirdStatusesForSelect()
  const consentModes = useGetConsentModesForSelect()
  const priorities = useGetPrioritiesForSelect({ variant: 'workspaceBidder' })
  const supplyChains = useGetSupplyChainsForSelect()

  return (
    <Paper>
      <SectionHeader
        subtitle={t('basicAttributes.header.subtitle')}
        title={t('basicAttributes.header.title')}
      />

      <GapBox />

      <BoxForm>
        <RadioGroup
          control={control}
          data-product-fruits={generateSelector({ step: 2, subTour: 2, tour: 3 })}
          disabled={variant === 'edit'}
          label={tc('common.accountType.label')}
          name='accountType'
          options={accountTypeOptions}
          row
          tooltip={{
            content: [
              tc('common.accountType.tooltip.header', {
                subject: tc('common.accountType.tooltip.subject.bidder'),
              }),
              tc('common.accountType.tooltip.paragraph1'),
              tc('common.accountType.tooltip.paragraph2'),
              tc('common.accountType.tooltip.paragraph3'),
            ],
          }}
        />

        {accountType === 'yieldbird' && variant === 'edit' && (
          <Select
            control={control}
            disabled={isFieldDisabled('status')}
            label={tc('common.status.label')}
            labelColor={({ value }: { value: YieldbirdStatus }) =>
              yieldbirdStatusColor({ status: value, theme })
            }
            name='status'
            options={statuses}
            tooltip={{
              content: tc('common.status.tooltip'),
            }}
          />
        )}

        {accountType === 'thirdPartyProvider' && (
          <Select
            control={control}
            disabled={variant === 'edit'}
            label={tc('common.supplyChain.label')}
            name='supplyChain'
            options={supplyChains}
            tooltip={{
              content: tc('common.supplyChain.tooltip.content'),
              link: {
                title: tc('common.supplyChain.tooltip.link'),
                url: generatePath(ROUTES.GLOBAL_SETUP.SCHAIN.LIST, { workspaceId }),
              },
            }}
          />
        )}

        <StackForm sx={{ alignItems: 'flex-start' }}>
          <Box>
            <SelectAutocomplete<CommonSchema, BidderSlice>
              control={control}
              data-product-fruits={generateSelector({ step: 3, subTour: 2, tour: 3 })}
              disabled={variant === 'edit' || isFieldDisabled('bidderId')}
              label={t('basicAttributes.bidderId.label')}
              name='bidderId'
              options={bidders}
            />

            <Hint
              content={t('basicAttributes.bidderId.hint')}
              contentEndAdornment={
                <Link
                  newTab
                  to={SELECT_HINT_LEARN_MORE}
                >
                  {tc('common:learnMore')}
                </Link>
              }
            />
          </Box>

          <TextField
            control={control}
            disabled={isFieldDisabled('bidderCode')}
            label={t('basicAttributes.bidderCode.label')}
            name='bidderCode'
            placeholder={t('basicAttributes.bidderCode.label')}
            tooltip={{
              content: t('basicAttributes.bidderCode.tooltip'),
            }}
          />
        </StackForm>

        <StackForm data-product-fruits={generateSelector({ step: 4, subTour: 2, tour: 3 })}>
          <Box>
            <TextField
              control={control}
              disabled={isFieldDisabled('bidderSid')}
              label={t('basicAttributes.bidderSid.label')}
              name='bidderSid'
              placeholder={t('basicAttributes.bidderSid.label')}
              tooltip={{
                content: t('basicAttributes.bidderSid.tooltip'),
              }}
            />

            {bidderId?.value !== undefined && bidderId.rawData?.sidRequired === false && (
              <Hint content={t('basicAttributes.bidderSid.hint', { bidderName: bidderId.label })} />
            )}
          </Box>

          <NumberField
            control={control}
            disabled={isFieldDisabled('bidCpmAdjustment')}
            label={t('basicAttributes.bidCpmAdjustment.label')}
            name='bidCpmAdjustment'
            tooltip={{
              content: t('basicAttributes.bidCpmAdjustment.tooltip'),
            }}
          />

          <Select
            control={control}
            disabled={isFieldDisabled('priority')}
            label={t('basicAttributes.priority.label')}
            name='priority'
            options={priorities}
            tooltip={{
              content: t('basicAttributes.priority.tooltip'),
            }}
          />

          <Select
            control={control}
            disabled={isFieldDisabled('consentMode')}
            label={t('basicAttributes.consentMode.label')}
            name='consentMode'
            options={consentModes}
            tooltip={{
              content: t('basicAttributes.consentMode.tooltip'),
            }}
          />
        </StackForm>
      </BoxForm>
    </Paper>
  )
}
